import { COLORS, SIZES, SPACING } from '@alto/design-library-tokens';
import { Column } from '@alto/design-system';
import React, { type ReactNode } from 'react';
import styled, { type CSSObject, css } from 'styled-components';
import getIsAuthorized from '~shared/features/auth/selectors/getIsAuthorized';
import { useSelectorShared } from '~shared/store';
import { useIsHubReferralInfoRoute, useIsOnboardingRoute, useIsProgynyWizardRoute } from '~web/hooks';

// media queries imported from deprecated ui library pocky
const sizes = {
  smDesktop: 1200,
  tablet: 767,
  smTablet: 601,
};
type Strings = TemplateStringsArray | CSSObject;
/* eslint-disable @alto/no-media-queries */
const media = {
  smDesktop: (strings: Strings, ...interpolations: any) => css`
    @media (max-width: ${sizes.smDesktop}px) {
      ${css(strings, ...interpolations)}
    }
  `,
  tablet: (strings: Strings, ...interpolations: any) => css`
    @media (max-width: ${sizes.tablet}px) {
      ${css(strings, ...interpolations)}
    }
  `,
  smTablet: (strings: Strings, ...interpolations: any) => css`
    @media (max-width: ${sizes.smTablet}px) {
      ${css(strings, ...interpolations)}
    }
  `,
};
/* eslint-enable @alto/no-media-queries */

export const FOOTER_HEIGHT = {
  PHONE: SIZES.PAGE.WEB_FOOTER_HEIGHT.SM,
  TABLET: SIZES.PAGE.WEB_FOOTER_HEIGHT.MD,
  DESKTOP: SIZES.PAGE.WEB_FOOTER_HEIGHT.LG,
};

const Wrapper = styled.div`
  min-height: 100vh;
`;

// min-height to keep footer at the bottom
// eslint-disable-next-line @alto/no-media-queries
const AuthorizedPageContainer = styled.div`
  min-height: calc(100vh - ${FOOTER_HEIGHT.DESKTOP});
  margin: 0 auto;
  ${media.tablet`
    min-height: calc(100vh - ${FOOTER_HEIGHT.TABLET});
  `};
  ${media.smTablet`
    min-height: calc(100vh - ${FOOTER_HEIGHT.PHONE});
  `};
`;

// min-height to keep footer at the bottom
const UnauthorizedPageContainer = styled.div<{
  withoutDashboardNavBar: boolean;
}>`
  padding-top: ${({ withoutDashboardNavBar }) =>
    withoutDashboardNavBar ? SPACING.STATIC.NONE : SIZES.PAGE.WEB_NAVBAR_HEIGHT.SM};
  min-height: calc(100vh - ${FOOTER_HEIGHT.PHONE});
`;

const narrowSmTabletCss = css`
  min-width: unset;
  width: 100%;
  padding: 0;
`;

// eslint-disable-next-line @alto/no-media-queries
const NarrowColumn = styled(Column)`
  min-width: 512px;
  width: 50%;
  margin: 0 auto;
  align-self: center;
  > * {
    width: 100%;
  }
  ${media.smTablet`${narrowSmTabletCss}`}
`;

// eslint-disable-next-line @alto/no-media-queries
const OnboardingFlexRow = styled(NarrowColumn)`
  ${media.smTablet`
    padding: 0 ${SPACING.STATIC.LG.px};
  `}
`;

type ContentProps = {
  readonly children: ReactNode;
};
/*
 * narrow single column layout
 * (if you want a single column layout that extends to the full width of the authorized/unauthorized page container you don't need SingleColumnContentWrapper)
 *
 * <SingleColumnContentWrapper>
 *   <div>foo</div>
 *   <div>bar</div>
 * </SingleColumnContentWrapper>
 *
 * +-------------+
 * |    +---+    |
 * |    |foo|    |
 * |    +---+    |
 * |    |bar|    |
 * |    +---+    |
 * +-------------+
 *
 */

export const SingleColumnContentWrapper = (props: ContentProps) => (
  <NarrowColumn
    gap="MD"
    wrap
    centerHorizontally
  >
    {props.children}
  </NarrowColumn>
);

export const OnboardingContentWrapper = (props: ContentProps) => (
  <OnboardingFlexRow
    gap="MD"
    wrap
    centerHorizontally
  >
    {props.children}
  </OnboardingFlexRow>
);

/*
 * basic content layout with sidebar
 * (if you want a single column layout that extends to the full width of the authorized/unauthorized page container you don't need ContentWrapper)
 *
 * <ContentWrapper>
 *    <Main>Stuff here</Main>
 *    <Sidebar>Sidebar Stuff here</Sidebar>
 * </ContentWrapper>
 *
 * +-------------+
 * | +------+--+ |
 * | | main |sb| |
 * | |      |  | |
 * | +------+--+ |
 * +-------------+
 *
 */
export const ContentWrapper = (props: { readonly children: ReactNode }) => (
  <Column
    gap="MD"
    wrap
  >
    {props.children}
  </Column>
);

export const Main = styled.div<{ wide?: boolean; children?: ReactNode }>`
  width: 100%;

  flex: ${({ wide }) => {
    return wide ? '2' : '3';
  }};
  > *:not(:last-child) {
    margin-bottom: ${SPACING.STATIC.MD.rem};
  }
`;

export const Sidebar = styled.div`
  flex: 1;
`;

// eslint-disable-next-line @alto/no-media-queries
export const PageBackground = styled.div<{
  white?: boolean;
  color?: string;
  fullWidth?: boolean;
  maxWidth?: string;
}>`
  background-color: ${({ white, color }) => {
    if (white) return COLORS.PALETTE.GREYSCALE.WHITE;
    if (color) return color;
    return COLORS.PALETTE.GREYSCALE.LIGHTER;
  }};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : '66.67%')};
  max-width: ${({ fullWidth, maxWidth }) => (fullWidth ? 'none' : maxWidth || '1170px')};
  min-height: calc(100vh - ${FOOTER_HEIGHT.DESKTOP} - ${SIZES.PAGE.WEB_NAVBAR_HEIGHT.LG});
  margin: 0 auto;
  padding: ${SPACING.STATIC.XL.rem} 0;
  ${media.smDesktop`
    width: 100%;
    padding: ${SPACING.STATIC.MD.rem};
  `};
  ${media.tablet`
    min-height: calc(100vh - ${FOOTER_HEIGHT.TABLET} - ${SIZES.PAGE.WEB_NAVBAR_HEIGHT.SM});
  `};
`;

type PageProps = {
  readonly children: ReactNode;
  readonly footer: ReactNode;
};

export const Page = ({ children, footer }: PageProps) => {
  const authorized = useSelectorShared(getIsAuthorized);
  const { isOnboardingRoute, isOrganicOnboardingRoute } = useIsOnboardingRoute();
  const { isHubReferralInfoRoute } = useIsHubReferralInfoRoute();
  const { isProgynyWizardRoute } = useIsProgynyWizardRoute();
  const withoutDashboardNavBar =
    isOnboardingRoute || isOrganicOnboardingRoute || isHubReferralInfoRoute || isProgynyWizardRoute;

  return (
    <Wrapper>
      {authorized ? (
        <AuthorizedPageContainer>{children}</AuthorizedPageContainer>
      ) : (
        <UnauthorizedPageContainer withoutDashboardNavBar={withoutDashboardNavBar}>
          {children}
        </UnauthorizedPageContainer>
      )}
      {footer}
    </Wrapper>
  );
};
